// The skill's total rule (quote-diag references/pricing-bands.md, "Rounding"):
// each GST-inclusive item is rounded to $10; then, where it can be reached
// without moving any item by more than about 1%, the total is nudged so it is
// divisible by 11, which lands the ex-GST subtotal on a whole $100 figure
// ($7,920 inc GST is $7,200 ex). Where it cannot be reached, the amounts stay
// as they are. Pure: the caller decides which rows may move and recomputes
// anything that depends on them (Quote Brain B3, fault N6).

export const CLEAN_SUBTOTAL_RULE = Object.freeze({ unit: 10, multiple: 110, maxItemShare: 0.01, maxDelta: 50 });

const cents = (v) => Math.round((Number(v) || 0) * 100);

/** True when a GST-inclusive total divides into a whole-$100 ex-GST subtotal. */
export function isCleanTotal(totalIncGst, rule = CLEAN_SUBTOTAL_RULE) {
    return cents(totalIncGst) % cents(rule.multiple) === 0;
}

/**
 * The nudges to try, smallest first: every multiple of the rounding unit up
 * to maxDelta either way, except zero. The caller applies each and keeps the
 * first whose recomputed total is clean, so a row that depends on the items
 * (an HBCF premium) is allowed for.
 */
export function cleanTotalDeltas(rule = CLEAN_SUBTOTAL_RULE) {
    const out = [];
    for (let d = rule.unit; d <= rule.maxDelta; d += rule.unit) out.push(-d, d);
    return out;
}

/**
 * Spread a nudge over rows = [{ id, amountIncGst, floorIncGst? }], largest
 * first, in whole rounding units, moving no row by more than maxItemShare of
 * its amount and none below its floor. Returns Map(id -> new amount), or null
 * when the rows cannot absorb the whole nudge.
 */
export function spreadTotalNudge(rows, delta, rule = CLEAN_SUBTOTAL_RULE) {
    const unitC = cents(rule.unit);
    let left = Math.abs(cents(delta));
    if (left % unitC !== 0) return null;
    const sign = Math.sign(delta);
    const out = new Map();
    const ordered = [...rows].filter((r) => cents(r.amountIncGst) > 0).sort((a, b) => cents(b.amountIncGst) - cents(a.amountIncGst) || String(a.id).localeCompare(String(b.id)));
    for (const r of ordered) {
        if (left === 0) break;
        const amountC = cents(r.amountIncGst);
        let room = Math.floor((amountC * rule.maxItemShare) / unitC) * unitC;
        if (sign < 0 && r.floorIncGst != null) room = Math.min(room, Math.max(0, amountC - cents(r.floorIncGst)));
        const take = Math.min(room - (room % unitC), left);
        if (take <= 0) continue;
        out.set(r.id, (amountC + sign * take) / 100);
        left -= take;
    }
    return left === 0 ? out : null;
}
